import { EntityIcon } from '@common/Entity/EntityIcons';
import { Entity } from '@common/types/Entity';
import { titleCase } from '@common/utils/metadataUtils';
import { BaseColor, theme } from '@prophecy/theme';
import {
  SlidingPanel,
  Button,
  StackItem,
  Stack,
  Ellipsis,
  Text,
  Centered,
  Spinner,
  Badges,
  Editor
} from '@prophecy/ui-v3';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { useRestQuery } from 'frontend/core/src/data/util';
import React from 'react';

import { fetchTranspilerComponentInfo } from '../../../data/apis/api';
import { Log, QueryKey, StatusMap, StatusMapKeys, StatusTone } from '../config';
import { StyledContent, StyledInfoCard, StyledSeparator } from '../ImportedInfo/components/RightInfoPanel';
import { useIsBulkTranspiledPipeline } from '../useIsBulkTranspiledPipeline';
import { Logs } from './Logs';

type Props = {
  onClose: () => void;
  componentId: string;
  transpilerId: string;
  pipelineName: string;
  projectName?: string;
};

export const ComponentDetail = ({ componentId, transpilerId, pipelineName, projectName, onClose }: Props) => {
  const isBulk = useIsBulkTranspiledPipeline()?.isBulk;
  const {
    data: componentData,
    isLoading,
    isError
  } = useRestQuery([QueryKey, componentId, transpilerId, pipelineName], () =>
    fetchTranspilerComponentInfo(transpilerId, componentId, pipelineName, isBulk)
  );

  const data = componentData?.data;
  const status = data?.status.toLowerCase() as StatusMapKeys;
  return (
    <SlidingPanel
      open={true}
      size='l'
      onClose={onClose}
      footer={
        <Button variant='secondaryGray' onClick={onClose} data-test-id='component-detail-close-button'>
          Close
        </Button>
      }>
      {isError || isLoading ? (
        <Centered>
          {isLoading ? <Spinner spinning={true} /> : <Badges tone='error'>Failed to load component details</Badges>}
        </Centered>
      ) : (
        <Stack gap={theme.spaces.x40} width='100%' height='100%'>
          <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
            {data?.name}
          </Text>
          <Stack gap={theme.spaces.x48} width='100%' grow='1' align='space-between' direction='horizontal'>
            <StackItem grow='1' shrink='1'>
              <Stack gap={theme.spaces.x24} width='100%' align='space-between' height='100%'>
                <Stack height='55%' width='100%'>
                  <Editor
                    ariaLabel={`Editor for import`}
                    language='xml'
                    path={'/transpiler'}
                    value={data?.content}
                    readOnly={true}
                  />
                </Stack>
                <Stack grow='1' width='100%'>
                  <Logs logs={data?.detailedLog || (FROZEN_EMPTY_ARRAY as Log[])} />
                </Stack>
              </Stack>
            </StackItem>
            <Stack width='250px' height='100%'>
              <StyledInfoCard hasHoverEffect={false}>
                <Stack gap={theme.spaces.x24} height='100%' width='100%'>
                  <Stack width='100%' direction='horizontal' align='space-between' alignY='center'>
                    <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                      Type
                    </Text>
                    <Text tone={theme.colors.gray900} level='sm'>
                      <StyledContent>
                        <Ellipsis tooltip={true}>{data?.componentType}</Ellipsis>
                      </StyledContent>
                    </Text>
                  </Stack>
                  <StyledSeparator orientation='horizontal' />
                  <Stack width='100%' direction='horizontal' align='space-between' alignY='center'>
                    <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                      Status
                    </Text>
                    {status ? (
                      <Badges tone={StatusTone[status] as BaseColor}>
                        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
                          {StatusMap[status]}
                          {titleCase(status)}
                        </Stack>
                      </Badges>
                    ) : (
                      <i>No content</i>
                    )}
                  </Stack>
                  <StyledSeparator orientation='horizontal' />
                  <Stack width='100%' direction='horizontal' align='space-between' alignY='center'>
                    <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                      File name
                    </Text>
                    <Text tone={theme.colors.gray900} level='sm'>
                      <StyledContent>
                        <Ellipsis tooltip={true}> {data?.fileName}</Ellipsis>
                      </StyledContent>
                    </Text>
                  </Stack>
                  <StyledSeparator orientation='horizontal' />
                  <Stack width='100%' direction='horizontal' align='space-between' alignY='center'>
                    <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                      Project
                    </Text>
                    <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
                      <EntityIcon entity={Entity.Project} size='xs' />
                      <Text level='sm'>
                        <StyledContent>
                          <Ellipsis tooltip={true}>{data?.projectName || projectName}</Ellipsis>
                        </StyledContent>
                      </Text>
                    </Stack>
                  </Stack>
                </Stack>
              </StyledInfoCard>
            </Stack>
          </Stack>
        </Stack>
      )}
    </SlidingPanel>
  );
};
